// @refresh reset
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { Container, LoadingDots } from 'components/ui'
import { ProductCarousel } from 'modules/products/components'
import Layout, { Breadcrumb, BreadcrumbProps } from 'components/layout'
import dynamic from 'next/dynamic'
import type { Experience, Product } from 'framework/products'
import { useEffect, useState, useCallback } from 'react'
import { FourInlineCards } from 'components/cards'
import parser, { parserExp } from 'fazendinha/product'
import sellerParser from 'fazendinha/seller'
import { ProductService } from 'modules/products/services'
import { ProductCard, ProductEventView } from 'modules/products/containers'
import { Seller } from 'framework/sellers'
import { SellerService } from 'modules/sellers/services'
import { ROUTE_PRODUCT_INDEX } from 'routes/products'
import s from './experience.module.css'

interface ExperiencePageProps {
  product: Experience
  slug: string
}

const dynamicProps = {
  loading: () => <LoadingDots />,
}

interface Params {
  params: {
    slug: string
  }
}

const SellerProfile = dynamic(() => import('modules/sellers/components/seller-profile'), dynamicProps)

export const getServerSideProps = async ({ params }: Params) => {
  const productService = new ProductService()

  try {
    const { slug } = params
    const {
      data: { data },
    } = await productService.getById(slug)

    return {
      props: {
        product: parserExp(data),
        slug,
      },
    }
  } catch (e) {
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
      props: {},
    }
  }
}

interface SellerProducts extends Array<Product> {}

function ExperiencePage({ product }: ExperiencePageProps) {
  const router = useRouter()

  const [seller, setSeller] = useState<Seller>(null as unknown as Seller)
  const [sellerProducts, setSellerProducts] = useState<SellerProducts>([] as SellerProducts)

  const getProducts = useCallback(() => {
    const productService = new ProductService()
    return productService.query({
      categories: '',
      page: 1,
      search: '',
      seller: product.seller.id,
    })
  }, [product.seller.id])

  const getSeller = useCallback(() => {
    const sellerService = new SellerService()
    return sellerService.getBySlug(product.seller.slug)
  }, [product.seller])

  useEffect(() => {
    getProducts().then(({ data: { data } }: { data: { data: any[] } }) => {
      setSellerProducts(data.map((p) => parser(p)))
    })
    getSeller().then(({ data: { data } }: { data: { data: any } }) => {
      setSeller(sellerParser(data as unknown as any))
    })
  }, [getProducts, getSeller])

  const crumbs: BreadcrumbProps['crumbs'] = [
    { url: `${ROUTE_PRODUCT_INDEX}/experiencias`, label: 'Experiências' },
    { label: `#${product.id}` },
  ]

  if (router.isFallback) {
    return <LoadingDots />
  }

  return (
    <Layout>
      <NextSeo
        title={product.name}
        description={product.description}
        openGraph={{
          type: 'website',
          title: product.name,
          description: product.description,
          images: [
            {
              url: product.image,
              width: 800,
              height: 600,
              alt: product.name,
            },
          ],
        }}
      />
      <Breadcrumb crumbs={crumbs} />
      <div className={s.view}>
        <ProductEventView key="product-event-view" product={{ ...product, seller }} />
      </div>
      <Container className={s.root}>
        <FourInlineCards
          cards={[
            {
              icon: <img src="/assets/icons/inline-cards/farm-products.svg" alt="" width="60px" />,
              title: 'Uma experiencia gastronomia',
              description: '',
            },
            {
              icon: <img src="/assets/icons/inline-cards/farm-tractor.svg" alt="" width="60px" />,
              title: 'Desfrute das belezas do lugar',
              description: '',
            },
            {
              icon: <img src="/assets/icons/inline-cards/milk-products.svg" alt="" width="60px" />,
              title: 'Leve para a casa',
              description: '',
            },
            {
              icon: <img src="/assets/icons/inline-cards/produtor.svg" alt="" width="60px" />,
              title: 'Se divirta',
              description: '',
            },
          ]}
        />
        {seller && (
          <div className={s.sellerSection}>
            <SellerProfile
              key="product-section-seller-profile"
              seller={seller}
              className={s.seller}
              withHeader={false}
            />
            {sellerProducts.length > 0 && (
              <ProductCarousel
                key="product-section-carousel"
                title="Mais produtos e experiências neste mesmo lugar"
                rightContent={{ href: `/produtores/${product.seller.slug}`, title: 'Veja todos' }}
                className={s.productCarousel}
              >
                {sellerProducts.map((sellerProduct) => (
                  <ProductCard key={`seller-product-${sellerProduct.id}`} product={sellerProduct} />
                ))}
              </ProductCarousel>
            )}
          </div>
        )}
      </Container>
    </Layout>
  )
}

export default ExperiencePage
